import { ReactElement, useState } from "react";
import TableContainer from "../../../../components/tableContainer";
import {
  FormControl,
  IconButton, MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../../configuration/configuration";
import { IKeyValue } from "../../types";
import StyledTableRow from "../../../../components/styledTableRow";
import Select, { SelectChangeEvent } from "@mui/material/Select";


interface IProps {
  params: IKeyValue[];
  setParams: (params: IKeyValue[]) => void;
  allowEdit: boolean;
}

interface IParamProps {
  index: number;
  param: IKeyValue;
  allowEdit: boolean;
  removeParam: (index: number) => void;
  params: IKeyValue[];
  rowEdited: number;
  setRowEdited: (rowEdited: number) => void;
}

const ParamRow = ({ index, param, allowEdit, removeParam, params, rowEdited, setRowEdited }: IParamProps): ReactElement => {
  const [force, setForce] = useState(0);
  const hasError = params.filter(x => x.key === param.key).length > 1 && index === rowEdited;

  const updateRow = (field: keyof IKeyValue, value: any) => {
    if (field === "key") {
      param[field] = value;
    } else if (field === "dataType") {
      param[field] = value;
      if(value === "TEXT") {
        param.value = "";
      } else if(value === "BOOL") {
        param.value = false;
      } else if (value === "NUMBER") {
        param.value = 0;
      }
    } else if (field === "value") {
      param[field] = value;
    }

    if(index === rowEdited) {
      setForce((force + 1) % 10 );
    } else {
      setRowEdited(index);
    }
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        {index + 1}
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <TextField
            variant="standard"
            size="small"
            value={param.key}
            type="text"
            slotProps={{ input: { readOnly: !allowEdit }}}
            error={hasError}
            onChange={(event) => updateRow("key",  event.target.value as string)}
          />
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={param.dataType}
            displayEmpty={true}
            disabled={!allowEdit}
            onChange={(event: SelectChangeEvent) => updateRow("dataType", event.target.value as string)}>
            <MenuItem value="TEXT">TEXT</MenuItem>
            <MenuItem value="NUMBER">NUMBER</MenuItem>
            {/*<MenuItem value="BOOL">BOOL</MenuItem>*/}
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          {param.dataType === "NUMBER"
            ? <TextField
              variant="standard"
              size="small"
              value={param.value}
              type="number"
              disabled={!allowEdit}
              slotProps={{ input: { readOnly: !allowEdit }}}
              onChange={(event) => updateRow("value", parseFloat(event.target.value as string))}/>
            : <TextField
              variant="standard"
              size="small"
              value={param.value}
              type="text"
              disabled={!allowEdit}
              onChange={(event) => updateRow("value", event.target.value as string)}/>
          }
        </FormControl>
      </TableCell>

      <TableCell>
        {allowEdit &&
          <IconButton style={{ padding: 0, margin: 0 }} onClick={() => removeParam(index)}>
            <Tooltip title={"Remove variable"}>
              <Delete style={{ color: getPalette().statusColor.error.background }}/>
            </Tooltip>
          </IconButton>
        }
      </TableCell>
    </StyledTableRow>
  );
};

const ParamsPanel = ({ params, setParams, allowEdit }: IProps): ReactElement => {
  const [ rowEdited, setRowEdited ] = useState(-1);

  const addField = () => {
    setParams([ ...params, { key: `param${params.length + 1}`, value: "", dataType: "TEXT" } ]);
  };

  const onRemoveParam = (index: number) => {
    params.splice(index, 1);
    setParams([ ...params ]);
  };

  return (
    <TableContainer>
      <Table stickyHeader={true} size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
              {allowEdit &&
                <IconButton style={{ maxWidth: 21, padding: 0, margin: 0 }} onClick={addField}>
                  <Tooltip title={"Add Parameter"}>
                    <Add style={{ color: getPalette().statusColor.info.background }}/>
                  </Tooltip>
                </IconButton>
              }
            </TableCell>
            <TableCell className="table-header">Name</TableCell>
            <TableCell className="table-header">Type</TableCell>
            <TableCell className="table-header">Value</TableCell>
            <TableCell className="table-header" />
          </TableRow>
        </TableHead>
        <TableBody>
          {params.map((row, index) => {

            return (
              <ParamRow key={`field-row-${index}`}
                removeParam={onRemoveParam}
                index={index}
                param={row}
                allowEdit={allowEdit}
                params={params}
                rowEdited={rowEdited}
                setRowEdited={setRowEdited}/>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ParamsPanel;